// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import Maintenance from './components/Maintenance';
import AcceptInvitation from './components/AcceptInvitation';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import AdminDashboard from './components/AdminDashboard';
import OperatorDashboard from './components/OperatorDashboard';
import ClientDashboard from './components/ClientDashboard';
import GuestBrandVoiceDesigner from './components/GuestBrandVoiceDesigner';
import LiveKitMeetingRoom from './components/LiveKitMeetingRoom';
import { LIVEKIT_ENABLED } from './config/features';
import { OrgBrandProvider } from './context/OrgBrandContext';
import { ModalProvider } from './context/ModalContext';
import { getUserRole, USER_ROLES } from './utils/rolePermissions';
import './App.css';

// Wrapper component to handle URL params for LiveKit meeting
function JoinMeetingWrapper({ user }) {
  const { meetingId } = useParams();
  const [searchParams] = useSearchParams();
  return (
    <LiveKitMeetingRoom
      meetingId={meetingId}
      user={user}
      agencyId={searchParams.get('agency_id')}
      onLeave={() => window.location.href = '/'}
    />
  );
}

// Configure axios defaults
axios.defaults.withCredentials = true;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Set up axios interceptor to handle session expiry and maintenance mode
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if server is in maintenance mode (503 Service Unavailable)
        if (error.response?.status === 503) {
          setMaintenanceMode(true);
          return Promise.reject(error);
        }

        // Check if user account has been disabled (403 with disabled flag)
        if (error.response?.status === 403 && error.response?.data?.disabled) {
          // Don't redirect if we're already on login page to prevent redirect loop
          const isLoginPage = window.location.pathname === '/';
          const isInvitationPage = window.location.pathname === '/accept-invitation';
          const isGuestPage = window.location.pathname === '/guest';

          setUser(null);

          if (!isLoginPage && !isInvitationPage && !isGuestPage) {
            // User was disabled while logged in
            // Clear session cookie first, then redirect
            axios.get('/auth/logout').finally(() => {
              window.location.href = '/?error=disabled';
            });
          }

          return Promise.reject(error);
        }

        // Check if it's a 401 Unauthorized error (session expired)
        if (error.response?.status === 401) {
          // Don't redirect if we're already on login page, accept-invitation page, guest page, or checking auth
          const isAuthCheck = error.config?.url?.includes('/api/user/profile');
          const isLoginPage = window.location.pathname === '/';
          const isInvitationPage = window.location.pathname === '/accept-invitation';
          const isGuestPage = window.location.pathname === '/guest';

          if (!isAuthCheck && !isLoginPage && !isInvitationPage && !isGuestPage) {
            // Session expired - clear user and redirect to login
            setUser(null);
            setAuthError('Your session has expired.\nPlease log in again.');
            window.location.href = '/';
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Check authentication status on app load
  useEffect(() => {
    // Skip auth check for guest routes - they handle their own auth
    if (window.location.pathname.startsWith('/guest')) {
      setLoading(false);
      return;
    }

    // Skip auth check if we're on login page with error parameter (disabled, auth_failed, etc)
    // Just show the error, don't make API calls that would trigger the error again
    const urlParams = new URLSearchParams(window.location.search);
    if (window.location.pathname === '/' && urlParams.has('error')) {
      const errorType = urlParams.get('error');
      const errorMessage = urlParams.get('message');

      // Display appropriate error message based on error type
      if (errorType === 'auth_failed') {
        setAuthError('Authentication failed.\nPlease try again.');
      } else if (errorType === 'unauthorized') {
        setAuthError('This is an invite-only application.\nPlease contact an administrator for access.');
      } else if (errorType === 'provider_mismatch') {
        setAuthError(errorMessage || 'This email is registered with a different sign-in method.');
      } else if (errorType === 'disabled') {
        setAuthError('Your account has been disabled.\nPlease contact Velvet admins.');
      }

      // Clean URL by removing query parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
      return;
    }

    checkAuthStatus();
  }, []);

  // Resume incomplete client recording uploads on app load (any Velvet page)
  useEffect(() => {
    if (!LIVEKIT_ENABLED) return;

    const resumeIncompleteUploads = async () => {
      try {
        const { initDB, getIncompleteUploads } = await import('./services/indexedDB');
        const uploadManager = (await import('./services/uploadManager')).default;

        await initDB();
        const incompleteMeetings = await getIncompleteUploads();

        if (incompleteMeetings.length > 0) {
          console.log(`[App] Resuming uploads for ${incompleteMeetings.length} incomplete meetings`);
          incompleteMeetings.forEach(meetingId => {
            uploadManager.resumeMeetingUpload(meetingId);
          });
        }
      } catch (error) {
        console.error('[App] Failed to resume incomplete uploads:', error);
      }
    };

    resumeIncompleteUploads();
  }, []);

  // Periodic session keep-alive to prevent silent token expiration
  // This triggers the backend's token refresh logic proactively
  useEffect(() => {
    if (!user) return; // Only run when user is logged in

    const keepAliveInterval = setInterval(async () => {
      try {
        await axios.get('/api/user/profile');
        // Session kept alive and token refreshed if needed on backend
      } catch (error) {
        // Session truly expired - interceptor will handle redirect
        console.log('Session keep-alive failed');
      }
    }, 10 * 60 * 1000); // Check every 10 minutes to keep session active

    return () => clearInterval(keepAliveInterval);
  }, [user]);

  // Re-check session when user returns to the tab (after being away)
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        try {
          await axios.get('/api/user/profile');
        } catch (error) {
          // Session expired while away - interceptor handles redirect
          console.log('Session check on tab focus failed');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get('/api/user/profile');
      setUser(response.data);
      setAuthError(null);
      setMaintenanceMode(false);
    } catch (error) {
      // Check if server is in maintenance mode (503 Service Unavailable)
      if (error.response?.status === 503) {
        setMaintenanceMode(true);
        setLoading(false);
        return;
      }

      // Silently handle auth failures - don't log for guest users or unauthenticated users
      setUser(null);
      // Check for auth error in URL
      const urlParams = new URLSearchParams(window.location.search);
      const errorType = urlParams.get('error');
      const errorMessage = urlParams.get('message');

      if (errorType === 'auth_failed') {
        setAuthError('Authentication failed.\nPlease try again.');
        // Clean URL by removing query parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (errorType === 'unauthorized') {
        // Display custom message from backend for unauthorized users (two lines)
        setAuthError('This is an invite-only application.\nPlease contact an administrator for access.');
        // Clean URL by removing query parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (errorType === 'provider_mismatch') {
        // Display custom message when user tries to login with wrong provider
        setAuthError(errorMessage || 'This email is registered with a different sign-in method.');
        // Clean URL by removing query parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (errorType === 'disabled') {
        // Display message for disabled account
        setAuthError('Your account has been disabled.\nPlease contact Velvet admins.');
        // Clean URL by removing query parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.get('/auth/logout');
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout locally even if server request fails
      setUser(null);
      window.location.href = '/';
    }
  };

  // Show maintenance page if in maintenance mode
  if (maintenanceMode) {
    return <Maintenance />;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }


  return (
    <ModalProvider>
      <OrgBrandProvider>
        <Router>
          <div className="App">
            <Routes>
            <Route
              path="/"
              element={
                user ? (() => {
                  const userRole = getUserRole(user);
                  switch (userRole) {
                    case USER_ROLES.SUPER_ADMIN:
                      return <Navigate to="/super-admin-dashboard" replace />;
                    case USER_ROLES.ADMIN:
                      return <Navigate to="/admin-dashboard" replace />;
                    case USER_ROLES.OPERATOR:
                      return <Navigate to="/operator-dashboard" replace />;
                    case USER_ROLES.CLIENT:
                      return <Navigate to="/client-dashboard" replace />;
                    default:
                      return <Navigate to="/" replace />;
                  }
                })() : (
                  <Login authError={authError} setAuthError={setAuthError} />
                )
              }
            />
          
          
          <Route
            path="/client-dashboard"
            element={
              user && getUserRole(user) === USER_ROLES.CLIENT ? (
                <ClientDashboard user={user} onLogout={handleLogout} onUserUpdate={checkAuthStatus} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/admin-dashboard"
            element={
              user && getUserRole(user) === USER_ROLES.ADMIN ? (
                <AdminDashboard user={user} onLogout={handleLogout} onUserUpdate={checkAuthStatus} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/operator-dashboard"
            element={
              user && getUserRole(user) === USER_ROLES.OPERATOR ? (
                <OperatorDashboard user={user} onLogout={handleLogout} onUserUpdate={checkAuthStatus} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/super-admin-dashboard"
            element={
              user && getUserRole(user) === USER_ROLES.SUPER_ADMIN ? (
                <SuperAdminDashboard user={user} onLogout={handleLogout} onUserUpdate={checkAuthStatus} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* Accept invitation route */}
          <Route
            path="/accept-invitation"
            element={<AcceptInvitation />}
          />

          {/* Guest brand voice designer route */}
          <Route
            path="/guest"
            element={<GuestBrandVoiceDesigner />}
          />

          {/* Join meeting route - public, allows guests. Disabled while LiveKit Meetings is off. */}
          <Route
            path="/join-meeting/:meetingId"
            element={LIVEKIT_ENABLED ? <JoinMeetingWrapper user={user} /> : <Navigate to="/" replace />}
          />

          {/* Maintenance preview route - for testing */}
          <Route
            path="/maintenance-preview"
            element={<Maintenance />}
          />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
      </OrgBrandProvider>
    </ModalProvider>
  );
}

export default App;